// src/pages/CommandListPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Button,
  TextField,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getAllCommands, deleteCommand } from '../services/commandService';
import { getAllExercises } from '../services/exerciseService';
import CreateCommandDialog from '../components/CreateCommandDialog';
import EditCommandDialog from '../components/EditCommandDialog';

import PageHeader from '../components/common/PageHeader';
import DataTable from '../components/common/DataTable';

const CommandListPage = () => {
  const navigate = useNavigate();

  const [commands, setCommands] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const [filters, setFilters] = useState({
    zone: '',
    type: '',
    status: '',
    exerciseId: '',
    search: '',
  });

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commandToDelete, setCommandToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // FIX: no setLoading(true) on refresh — keeps DataTable mounted, preserves page
  const fetchCommands = useCallback(async () => {
    setError('');
    try {
      const response = await getAllCommands(filters);
      setCommands(response.data.commands);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load commands');
    }
  }, [filters]);

  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    const load = async () => {
      setError('');
      try {
        // Load exercises first so we can default the filter to the ACTIVE one
        const exRes = await getAllExercises();
        const exList = exRes.data.exercises || [];
        setExercises(exList);

        const active = exList.find((e) => e.status === 'ACTIVE');
        const scopedFilters = active
          ? { ...filters, exerciseId: active.id }
          : filters;
        if (active) setFilters(scopedFilters);

        const cmdRes = await getAllCommands(scopedFilters);
        setCommands(cmdRes.data.commands);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load commands');
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };
    if (initialLoad) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleApplyFilters = () => {
    fetchCommands();
  };

  const handleClearFilters = () => {
    setFilters({ zone: '', type: '', status: '', exerciseId: '', search: '' });
  };

  const handleEdit = (command) => {
    setSelectedCommand(command);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (command) => {
    setCommandToDelete(command);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    setError('');
    const label = `${commandToDelete?.code || ''} - ${commandToDelete?.name || ''}`.trim();
    try {
      await deleteCommand(commandToDelete.id);
      setDeleteDialogOpen(false);
      setCommandToDelete(null);
      await fetchCommands();
      notify(`Command ${label} deleted successfully`, 'success');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete command';
      setError(msg);
      notify(msg, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'IN_PROGRESS': return 'primary';
      case 'COMPLETED': return 'success';
      case 'NOT_STARTED': return 'default';
      default: return 'default';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'STATE_COMMAND': return 'primary';
      case 'SPECIAL_LOCATION': return 'secondary';
      case 'HEADQUARTERS': return 'error';
      default: return 'default';
    }
  };

  const columns = [
    { id: 'code', label: 'Code', render: (row) => <strong>{row?.code || '-'}</strong> },
    { id: 'name', label: 'Name', render: (row) => row?.name || '-' },
    { id: 'zone', label: 'Zone', render: (row) => row?.zone || '-' },
    { id: 'state', label: 'State', render: (row) => row?.state || '-' },
    {
      id: 'type', label: 'Type',
      render: (row) => row?.type
        ? <Chip label={row.type.replace('_', ' ')} color={getTypeColor(row.type)} size="small" />
        : '-',
    },
    {
      id: 'status', label: 'Status',
      render: (row) => row?.status
        ? <Chip label={row.status.replace('_', ' ')} color={getStatusColor(row.status)} size="small" />
        : '-',
    },
    { id: 'exercise', label: 'Exercise', render: (row) => row?.exercise?.year || '-' },
    { id: 'personnel', label: 'Personnel', render: (row) => row?._count?.personnel || 0 },
  ];

  const rowActions = (row) => [
    { icon: <EditIcon fontSize="small" />, label: 'Edit', onClick: () => handleEdit(row) },
    { icon: <DeleteIcon fontSize="small" />, label: 'Delete', color: 'error', onClick: () => handleDeleteClick(row) },
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <PageHeader
          title="Command Management"
          onBack={() => navigate('/')}
          actions={[
            { label: 'Refresh', icon: <RefreshIcon />, onClick: fetchCommands, variant: 'outlined' },
            { label: 'Create Command', icon: <AddIcon />, onClick: () => setCreateDialogOpen(true), variant: 'contained' },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField label="Search" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Code, name, or state" sx={{ minWidth: 250 }} size="small" />
            <TextField select label="Zone" name="zone" value={filters.zone} onChange={handleFilterChange} sx={{ minWidth: 200 }} size="small">
              <MenuItem value="">All Zones</MenuItem>
              <MenuItem value="ZONE A - LAGOS">Zone A - Lagos</MenuItem>
              <MenuItem value="ZONE B - KADUNA">Zone B - Kaduna</MenuItem>
              <MenuItem value="ZONE B2 - KADUNA">Zone B2 - Kaduna</MenuItem>
              <MenuItem value="ZONE C - BAUCHI">Zone C - Bauchi</MenuItem>
              <MenuItem value="ZONE D - MINNA">Zone D - Minna</MenuItem>
              <MenuItem value="ZONE E - OWERRI">Zone E - Owerri</MenuItem>
              <MenuItem value="ZONE F - IBADAN">Zone F - Ibadan</MenuItem>
              <MenuItem value="ZONE G - BENIN">Zone G - Benin</MenuItem>
              <MenuItem value="ZONE H - BENUE">Zone H - Benue</MenuItem>
              <MenuItem value="SERVICE HEADQUARTERS">Service HQ</MenuItem>
            </TextField>
            <TextField select label="Type" name="type" value={filters.type} onChange={handleFilterChange} sx={{ minWidth: 200 }} size="small">
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="STATE_COMMAND">State Command</MenuItem>
              <MenuItem value="SPECIAL_LOCATION">Special Location</MenuItem>
              <MenuItem value="HEADQUARTERS">Headquarters</MenuItem>
            </TextField>
            <TextField select label="Status" name="status" value={filters.status} onChange={handleFilterChange} sx={{ minWidth: 200 }} size="small">
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="NOT_STARTED">Not Started</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
            </TextField>
            <TextField select label="Exercise" name="exerciseId" value={filters.exerciseId} onChange={handleFilterChange} sx={{ minWidth: 200 }} size="small">
              <MenuItem value="">All Exercises</MenuItem>
              {exercises.map((exercise) => (
                <MenuItem key={exercise.id} value={exercise.id}>
                  {exercise.year} - {exercise.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleApplyFilters} size="small">Apply Filters</Button>
            <Button variant="outlined" onClick={handleClearFilters} size="small">Clear Filters</Button>
          </Box>
        </Paper>

        <DataTable
          columns={columns}
          data={commands}
          loading={loading}
          rowActions={rowActions}
          emptyType="commands"
          emptyMessage="No commands found. Create a new command or adjust your filters."
        />

        {!loading && commands.length > 0 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Total commands: <strong>{commands.length}</strong>
            </Typography>
          </Box>
        )}
      </Box>

      <CreateCommandDialog
        open={createDialogOpen}
        exercises={exercises}
        onNotify={notify}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => { setCreateDialogOpen(false); fetchCommands(); }}
      />

      {selectedCommand && (
        <EditCommandDialog
          open={editDialogOpen}
          command={selectedCommand}
          onNotify={notify}
          onClose={() => { setEditDialogOpen(false); setSelectedCommand(null); }}
          onSuccess={() => { setEditDialogOpen(false); setSelectedCommand(null); fetchCommands(); }}
        />
      )}

      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Command</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete <strong>{commandToDelete?.code} - {commandToDelete?.name}</strong>?
          </Alert>
          {commandToDelete?._count?.personnel > 0 && (
            <Alert severity="error">
              This command has {commandToDelete._count.personnel} associated personnel records. You must delete or reassign them first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading || commandToDelete?._count?.personnel > 0}>
            {deleteLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CommandListPage;
